import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import FAQ from '@/components/base/FAQ';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles
    .filter((a) => a.categorySlug === article?.categorySlug && a.slug !== slug)
    .slice(0, 3);

  const headings = useMemo(() => {
    if (!article) return [];
    const regex = /## (.+)/g;
    const matches: string[] = [];
    let match;
    while ((match = regex.exec(article.content)) !== null) {
      matches.push(match[1]);
    }
    return matches;
  }, [article]);

  const [activeHeading, setActiveHeading] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const headingElements = document.querySelectorAll('h2');
      let current = '';
      headingElements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top <= 120) {
          current = el.textContent || '';
        }
      });
      setActiveHeading(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您访问的文章不存在或已被移除。</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    image: article.image,
    url: `${window.location.origin}/article/${article.slug}`,
    publisher: {
      '@type': 'Organization',
      name: t('siteName'),
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Article Header */}
          <section className="bg-background-100 border-b border-background-200">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
              <Breadcrumb
                items={[
                  { label: article.category, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
              <div className="flex flex-wrap items-center gap-3 mt-4 mb-4">
                <span className="inline-flex items-center gap-1 bg-accent-100 text-accent-700 text-xs px-3 py-1 rounded-full font-medium">
                  <i className="ri-folder-line"></i>
                  <Link to={`/category/${article.categorySlug}`} className="hover:underline">
                    {article.category}
                  </Link>
                </span>
                <span className="text-xs text-foreground-400">
                  <i className="ri-time-line mr-1"></i>
                  {article.readTime} 分钟阅读
                </span>
                <span className="text-xs text-foreground-400">
                  <i className="ri-calendar-line mr-1"></i>
                  {article.publishedAt}
                </span>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4 font-heading leading-tight">
                {article.title}
              </h1>
              <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
                {article.excerpt}
              </p>
              <div className="flex items-center gap-3 mt-6 pt-6 border-t border-background-200">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-sm">
                  {article.author.charAt(0)}
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground-900">{article.author}</div>
                  <div className="text-xs text-foreground-400">{t('article.author')}</div>
                </div>
              </div>
            </div>
          </section>

          {/* Article Body */}
          <section className="py-10 md:py-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="flex gap-10">
                {/* Table of Contents - Desktop */}
                <aside className="hidden lg:block w-56 flex-shrink-0">
                  <div className="sticky top-24">
                    <h4 className="text-sm font-bold text-foreground-900 mb-4 font-heading">
                      {t('article.tableOfContents')}
                    </h4>
                    <nav className="space-y-1 border-l-2 border-background-200">
                      {headings.map((heading, i) => (
                        <a
                          key={i}
                          href={`#heading-${i}`}
                          className={`block py-1.5 pl-4 text-sm transition-colors duration-200 border-l-2 -ml-0.5 ${
                            activeHeading === heading
                              ? 'border-primary-500 text-primary-600 font-medium'
                              : 'border-transparent text-foreground-500 hover:text-foreground-800'
                          }`}
                        >
                          {heading}
                        </a>
                      ))}
                    </nav>
                  </div>
                </aside>

                {/* Article Content */}
                <div className="flex-1 min-w-0">
                  <div className="rounded-lg overflow-hidden mb-10">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full aspect-video object-cover"
                    />
                  </div>

                  <div
                    className="prose-content"
                    dangerouslySetInnerHTML={{
                      __html: article.content.replace(
                        /## (.+)/g,
                        (_, title, offset) => {
                          const headingIndex = headings.indexOf(title);
                          return `<h2 id="heading-${headingIndex}">${title}</h2>`;
                        }
                      ),
                    }}
                  />

                  {/* Tags */}
                  <div className="flex flex-wrap items-center gap-2 mt-10 pt-8 border-t border-background-200">
                    <span className="text-xs text-foreground-400 mr-2">{t('article.tags')}：</span>
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* FAQ */}
                  {article.faqs && article.faqs.length > 0 && (
                    <div className="mt-12">
                      <h2 className="text-xl font-bold text-foreground-950 mb-6 font-heading">
                        常见问题
                      </h2>
                      <FAQ items={article.faqs} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="py-14 md:py-18 bg-background-100">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-8 font-heading text-center">
                  {t('article.relatedArticles')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedArticles.map((rel) => (
                    <ArticleCard key={rel.slug} article={rel} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}